import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface WebsitesAccordionProps {
  control: Control<UserFormData>;
  disabled?: boolean;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
}

export default function WebsitesAccordion({
  control,
  errors,
  disabled,
  watch,
}: WebsitesAccordionProps) {
  const [open, setOpen] = useState(false);
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'websites',
  });

  const menuItems = [
    { value: 'app_install_page', label: 'App Install Page' },
    { value: 'blog', label: 'Blog' },
    { value: 'custom', label: 'Custom' },
    { value: 'ftp', label: 'FTP' },
    { value: 'home', label: 'Home' },
    { value: 'home_page', label: 'Home Page' },
    { value: 'other', label: 'Other' },
    { value: 'profile', label: 'Profile' },
    { value: 'reservations', label: 'Reservations' },
    { value: 'resume', label: 'Resume' },
    { value: 'work', label: 'Work' },
  ];

  const websitesErrors = errors.websites;

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (websitesErrors) {
      setOpen(true);
    }
  }, [websitesErrors]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        if (fields.length === 0) return;
        setOpen(!open);
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton
            component="span"
            disabled={fields.length === 0}
            sx={{ mr: -1 }}
            disableRipple
          >
            <ExpandMore />
          </IconButton>
        }
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: (theme) =>
                websitesErrors ? theme.palette.error.main : 'inherit',
            }}
          >
            Websites
          </Typography>
          <Tooltip title="Add" arrow>
            <span>
              <IconButton
                component="span"
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  append({
                    type: '',
                    primary: false,
                    customType: '',
                    url: '',
                  });
                }}
              >
                <AddCircle />
              </IconButton>
            </span>
          </Tooltip>
          {fields.length > 0 ? (
            <Chip label={fields.length} color="primary" size="small" />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        {fields.map((item, index) => {
          const typeField = watch(`websites.${index}.type`);

          return (
            <Box key={item.id}>
              <Stack
                gap={1}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ my: 1.5 }}
              >
                <Stack flexDirection="row" alignItems="center" gap={2}>
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`websites.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `websites-type-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `websites-type-${index}`,
                        }}
                        name="websites"
                        select
                        disabled={disabled}
                        label="Type"
                        error={!!websitesErrors?.[index]?.type}
                        helperText={
                          websitesErrors?.[index]?.type
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {typeField === 'custom' && (
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`websites.${index}.customType`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `websites-customType-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `websites-customType-${index}`,
                          }}
                          disabled={disabled}
                          name="websites"
                          label="Custom Type"
                          error={!!websitesErrors?.[index]?.customType}
                          helperText={
                            websitesErrors?.[index]?.customType
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 225,
                          }}
                        />
                      )}
                    />
                  )}
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`websites.${index}.url`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        name="websites"
                        inputRef={field.ref}
                        error={!!websitesErrors?.[index]?.url}
                        helperText={
                          websitesErrors?.[index]?.url
                            ? 'This field is required.'
                            : ''
                        }
                        disabled={disabled}
                        autoComplete="off"
                        label="Url"
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                </Stack>

                {/* actions */}
                <Stack flexDirection="row" gap={2}>
                  <Tooltip title="Move Down" arrow>
                    <span>
                      <IconButton
                        component="span"
                        disabled={index === fields.length - 1 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index + 1);
                        }}
                      >
                        <ArrowDownward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move Up" arrow>
                    <span>
                      <IconButton
                        component="span"
                        disabled={index === 0 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index - 1);
                        }}
                      >
                        <ArrowUpward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        component="span"
                        size="small"
                        disabled={disabled}
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {index !== fields.length - 1 && <Divider />}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
